import React, { useState, useMemo } from 'react';
import { 
  Languages, 
  Search, 
  Edit, 
  Save, 
  Plus, 
  Trash2 
} from 'lucide-react';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Language Translation Interface
interface LanguageTranslation {
  id: string;
  key: string;
  defaultText: string;
  translations: {
    [languageCode: string]: string;
  };
}

const LanguageManagementPage: React.FC = () => {
  useBreadcrumbs([
        { label: 'General Settings', href: '#' },
        { label: 'Language Management' }
      ]);
  // Language Translations State
  const [languageTranslations, setLanguageTranslations] = useState<LanguageTranslation[]>([
    {
      id: 'trans001',
      key: 'welcome_message',
      defaultText: 'Welcome to our platform',
      translations: {
        es: 'Bienvenido a nuestra plataforma',
        fr: 'Bienvenue sur notre plateforme',
        de: 'Willkommen auf unserer Plattform',
        zh: '欢迎使用我们的平台'
      }
    },
    {
      id: 'trans002',
      key: 'login_button',
      defaultText: 'Log In',
      translations: {
        es: 'Iniciar sesión',
        fr: 'Se connecter',
        de: 'Anmelden',
        zh: '登录'
      }
    }
  ]);

  // Filters State
  const [filters, setFilters] = useState({
    searchTerm: '',
    language: ''
  });

  // Filtered Language Translations
  const filteredLanguageTranslations = useMemo(() => {
    return languageTranslations.filter(translation => 
      translation.key.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      translation.defaultText.toLowerCase().includes(filters.searchTerm.toLowerCase())
    );
  }, [languageTranslations, filters.searchTerm]);

  // Add New Translation Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newTranslation, setNewTranslation] = useState<Partial<LanguageTranslation>>({
    key: '',
    defaultText: '',
    translations: {}
  });

  // Handle Add New Translation
  const handleAddTranslation = () => {
    if (!newTranslation.key || !newTranslation.defaultText) {
      alert('Please fill in key and default text');
      return;
    }

    const newEntry: LanguageTranslation = {
      id: `trans${languageTranslations.length + 1}`,
      key: newTranslation.key,
      defaultText: newTranslation.defaultText,
      translations: newTranslation.translations || {}
    };

    setLanguageTranslations([...languageTranslations, newEntry]);
    setIsAddModalOpen(false);
    setNewTranslation({});
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Languages className="h-7 w-7 mr-3 text-blue-600" />
                Language Management
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage translations across multiple languages
              </p>
            </div>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add New Translation
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex space-x-4">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search translations..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            />
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          </div>
          <SearchableSelect
            options={[
              { value: '', label: 'All Languages' },
              { value: 'es', label: 'Spanish' },
              { value: 'fr', label: 'French' },
              { value: 'de', label: 'German' },
              { value: 'zh', label: 'Chinese' }
            ]}
            value={filters.language}
            onChange={(value) => setFilters({ ...filters, language: value })}
            placeholder="Language"
            icon={Languages}
            className="w-48"
          />
        </div>

        {/* Translations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default Text
                </th>
                {Object.keys(languageTranslations[0]?.translations || {}).map(lang => (
                  <th key={lang} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {lang.toUpperCase()}
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLanguageTranslations.map((translation) => (
                <tr key={translation.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {translation.key}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {translation.defaultText}
                  </td>
                  {Object.entries(translation.translations).map(([lang, text]) => (
                    <td key={lang} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {text}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                        title="Edit Translation"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900 flex items-center"
                        title="Delete Translation"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add Translation Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Add New Translation
                </h3>
                <div className="mt-2 px-7 py-3">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Translation Key
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newTranslation.key || ''}
                      onChange={(e) => setNewTranslation({ ...newTranslation, key: e.target.value })}
                      placeholder="Enter translation key"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Text
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={newTranslation.defaultText || ''}
                      onChange={(e) => setNewTranslation({ ...newTranslation, defaultText: e.target.value })}
                      placeholder="Enter default text"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 px-4 py-3">
                  <button
                    onClick={() => setIsAddModalOpen(false)}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTranslation}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Translation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageManagementPage;